import React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import Alert from '@material-ui/lab/Alert';
import Loader from '../../../components/Loader';
import { ProviderAPI } from '../../../api';
import equal from 'fast-deep-equal/es6/react';
import { dataDiff } from '../../../lib/util';
import PremisesEditForm from './PremisesEditForm';
import TaskComplete from '../../../components/dialogues/TaskComplete';

const EditPremises = ({providerUuid, premiseUuid=null, back}) => {
  const [busy, setBusy] = React.useState(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertState, setAlterState] = React.useState(null);
  const [alertCaption, setAlterCaption] = React.useState('');
  const [initial, setInitial] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [completed, setCompleted] = React.useState(false);

  const getDataForUpdate = async () => {
    try {
      const premises = await ProviderAPI.getPremise(providerUuid, premiseUuid);
      setInitial(premises.payload);
      setData(premises.payload);
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  };

  const getDataForCreate = async () => {
    const initialData = {
      name: '',
      active: false,
      address_line_1: '',
      address_line_2: '',
      suburb: '',
      state: '',
      post_code: '',
      phone: '',
      email: '',
    };
    setInitial(initialData);
    setData(initialData);
  };

  const triggerAlert = (caption, variant) => {
    setAlterCaption(caption);
    setAlterState(variant);
    setAlertOpen(true);
  };

  const closeAlert = () => {
    setAlertOpen(false);
  };

  const handleChange = (event, field) => {
    let updatedData;
    const value = (event.target === undefined) ? event : event.target.value;
    if (field === 'active') {
      const translatedValue = (value === 'yes') ? true : false;
      updatedData = update(data, {[field]: {$set: translatedValue}});
    } else {
      updatedData = update(data, {[field]: {$set: value}});
    }
    setData(updatedData);
  };

  const saveUpdate = async () => {
    try {
      // Only submit what has changed plus a combiend 'address' field for geocoding
      const patchData = dataDiff(initial, data);
      patchData.address = `${data.address_line_1} ${data.address_line_2} ${data.suburb} ${data.state} ${data.post_code}`;
      await ProviderAPI.updatePremise(providerUuid, premiseUuid, patchData);
      setInitial(data);
      triggerAlert('Premises successfully updated.', 'success');
    } catch (err) {
      triggerAlert('Update failed. Check you have entered all information correctly.', 'warning');
      setBusy(false);
    }
  };

  const saveCreate = async () => {
    try {
      data.address = `${data.address_line_1} ${data.address_line_2} ${data.suburb} ${data.state} ${data.post_code}`;
      await ProviderAPI.createPremise(providerUuid, data);
      setBusy(false);
      setCompleted(true);
    } catch (err) {
      triggerAlert('Premise creation failed. Check you have entered all required sections including a full address.', 'warning');
    }
  };

  const save = async () => {
    if (equal(initial, data)) {
      triggerAlert('No data has changed.', 'info');
      return null;
    }
    setBusy(true);
    if ('uuid' in initial) {
      saveUpdate();
    } else {
      saveCreate();
    }
    setBusy(false);
  };

  React.useEffect(() => {
    if (premiseUuid === null) {
      getDataForCreate();
    } else {
      getDataForUpdate();
    }
  }, []); // eslint-disable-line

  if (data === null) {
    return (<Loader />);
  }

  const completionMessage = `Your new Premise called '${data.name}' has been created. It won't be visible on the platform until you set the active selection to 'Yes'.`;

  return (
    <PremisesEditForm
      premise={data}
      handleChange={handleChange}
      busy={busy}
      back={back}
      save={save}
    >
      {alertOpen ? (
        <Alert severity={alertState} onClose={closeAlert}>
          {alertCaption}
        </Alert>
      ) : null}
      <TaskComplete
        open={completed}
        next={back}
        title="New Premise created"
        message={completionMessage}
      />
    </PremisesEditForm>
  );
};

EditPremises.propTypes = {
  providerUuid: PropTypes.string.isRequired,
  premiseUuid: PropTypes.string,
  back: PropTypes.func.isRequired,
};

export default EditPremises;
